import {
  Constructor,
  EdgeStyleBase,
  GeneralPath,
  IEdge,
  IInputModeContext,
  INode,
  IObstacleProvider,
  IRenderContext,
  Point,
  SvgVisual,
  Visual,
} from '@ardoq/yfiles';
import { getChangedPopover } from 'scope/modelUtil';
import { classes, getCssClassFromDiffType } from '@ardoq/common-helpers';
import { createSvgElement } from '@ardoq/dom-utils';
import { DiffType } from '@ardoq/data-model';
import { LineType } from '@ardoq/api-types';
import { modelOperations } from '@ardoq/core';
import { createPath, toPathPieces } from 'yfilesExtensions/edgeUtil';
import SparkMD5 from 'spark-md5';
import { DATA_RENDER_HASH } from 'yfilesExtensions/styles/consts';
import {
  type GraphEdgeBase,
  disposeCallback,
  isComponentGraphNode,
  isAnyComponentGraphEdge,
  isAggregatedGraphEdge,
  isParentChildGraphEdge,
} from '@ardoq/graph';
import { logError } from '@ardoq/logging';
import { BasicEdgeObstacleProvider } from 'yfilesExtensions/BasicEdgeObstacleProvider';
import { BULK_POPOVER_ATTR } from '@ardoq/popovers';
import { MODEL_ID_ATTRIBUTE } from 'consts';
import { SMOOTH_PATH_OPTIONS } from 'tabview/relationshipDiagrams/consts';
import { NO_EXPORT_CLASS_NAME } from '@ardoq/global-consts';
import { getElementOpacityIfTransparentized } from '../utils';
import { FILTER_DIMENSION_WORKAROUND_CSS_CLASS } from 'tabview/graphComponent/atoms';
import {
  anchorDashes,
  type PathPiece,
} from 'tabview/canvasRendering/anchoredDashes';

const createRenderDataCache = (
  edge: GraphEdgeBase,
  pathData: string,
  hoverPathData: string,
  sourceNode: INode | null,
  targetNode: INode | null
) =>
  SparkMD5.hash(
    `${edge.getLineBeginning()},${edge.getLineEnding()},${edge.getCSS({
      useAsBackgroundStyle: false,
    })},${pathData},${hoverPathData},${edge.getVisualDiffType()},${String(
      isGhostNode(targetNode) || isGhostNode(sourceNode)
    )},${edge.isTransparentized}`
  );
const validateGraphEdge = (graphEdge: object): graphEdge is GraphEdgeBase => {
  if (!isAnyComponentGraphEdge(graphEdge)) {
    logError(Error('Unexpected edge type.'));
    return false;
  }
  return true;
};

const isGhostNode = (node: any) => isComponentGraphNode(node) && node.isGhost;

const getModelIds = (graphEdge: GraphEdgeBase) =>
  isParentChildGraphEdge(graphEdge)
    ? []
    : isAggregatedGraphEdge(graphEdge)
      ? graphEdge.modelIds
      : [graphEdge.modelId];

const toDashArray = (line: LineType): number[] =>
  modelOperations.lineTypeToStrokeDasharray(line)?.split(',').map(Number) ?? [];

const toSvgPathData = (piece: PathPiece): string => {
  const [fromX, fromY] = piece.from;
  const [toX, toY] = piece.to;
  if (piece.type === 'line') {
    return `M ${fromX} ${fromY} L ${toX} ${toY}`;
  }
  const [controlX, controlY] = piece.control;
  return `M ${fromX} ${fromY} Q ${controlX} ${controlY} ${toX} ${toY}`;
};

const styleEdgeStroke = (
  element: SVGElement,
  isTransparentized: boolean,
  diffType: DiffType
) => {
  element.style.strokeOpacity = `${getElementOpacityIfTransparentized(
    isTransparentized,
    0.3
  )}`;
  element.style.fill = 'none';
  switch (diffType) {
    case DiffType.ADDED:
    case DiffType.REMOVED:
    case DiffType.CHANGED:
    case DiffType.UNCHANGED:
      element.style.stroke = 'black';
      element.style.color = 'hsl(0, 0%, d50%)';
      break;
  }
};

const createDashedPieceElements = (
  smoothedPath: GeneralPath,
  dashArray: number[],
  className: string
): SVGElement[] =>
  anchorDashes(toPathPieces(smoothedPath), dashArray).map(
    ({ piece, dashOffset }) => {
      const element = createSvgElement('path', {
        d: toSvgPathData(piece),
        class: className,
      });
      element.style.strokeDashoffset = `${dashOffset}`;
      return element;
    }
  );

const render = (
  edge: IEdge,
  container: SVGElement,
  path: GeneralPath,
  hoverPath: GeneralPath,
  cache: string
) => {
  container.innerHTML = '';
  container.setAttribute(DATA_RENDER_HASH, cache);
  const { sourceNode, targetNode, tag: graphEdge } = edge;
  if (!sourceNode || !targetNode || !validateGraphEdge(graphEdge)) {
    logError(Error('Invalid edge.'));
    return;
  }
  const { tag: sourceGraphNode } = sourceNode;
  const { tag: targetGraphNode } = targetNode;

  const isGhostReference =
    isGhostNode(sourceGraphNode) || isGhostNode(targetGraphNode);

  const diffType = isGhostReference
    ? DiffType.UNCHANGED
    : graphEdge.getVisualDiffType();
  const visualDiffClassName = getCssClassFromDiffType(diffType);
  container.setAttribute('class', classes('integration', visualDiffClassName));
  const modelIds = getModelIds(graphEdge);
  const tooltipAttributes = Object.entries(
    modelIds.length === 1 ? getChangedPopover(modelIds[0], diffType) : {}
  );

  if (tooltipAttributes.length > 0) {
    tooltipAttributes.forEach(([key, value]) =>
      container.setAttribute(key, value)
    );
  } else {
    [BULK_POPOVER_ATTR, MODEL_ID_ATTRIBUTE].forEach(key =>
      container.removeAttribute(key)
    );
  }

  container.dataset.referenceModelIds = modelIds.join(',');
  const smoothedPath = path.createSmoothedPath(SMOOTH_PATH_OPTIONS);
  const pathData = smoothedPath.createSvgPathData();
  const hoverPathData = hoverPath
    .createSmoothedPath(SMOOTH_PATH_OPTIONS)
    .createSvgPathData();

  if (diffType !== DiffType.NONE) {
    // Workaround an issue with svg filters.
    // If the path is a straight line, meaning the path has no area, applying
    // a filter will make the path disapper.
    container.appendChild(
      createSvgElement('rect', {
        class: FILTER_DIMENSION_WORKAROUND_CSS_CLASS,
        x: `${edge.sourcePort!.location.x - 15}`,
        y: `${edge.sourcePort!.location.y - 15}`,
        width: '30',
        height: '30',
      })
    );
  }

  const isParentChildReference = isParentChildGraphEdge(graphEdge);
  const className = graphEdge.getCSS({ useAsBackgroundStyle: false });
  const hoverPathElement = createSvgElement('path', {
    d: hoverPathData,
    class: classes(
      className,
      'hoverPath',
      NO_EXPORT_CLASS_NAME,
      'skipContextUpdate',
      isParentChildReference && 'parent-child-reference'
    ),
  });

  container.appendChild(hoverPathElement);

  const edgePathElement = createSvgElement('path', {
    d: pathData,
    class: className,
    'marker-start': `url(#${graphEdge.getLineBeginning()})`,
    'marker-end': `url(#${graphEdge.getLineEnding()})`,
  });
  const appendEdgeStroke = (element: SVGElement) => {
    styleEdgeStroke(element, graphEdge.isTransparentized, diffType);
    container.appendChild(element);
  };
  const dashArray = toDashArray(graphEdge.getLine());
  if (!dashArray.length) {
    appendEdgeStroke(edgePathElement);
    return;
  }
  createDashedPieceElements(smoothedPath, dashArray, className).forEach(
    appendEdgeStroke
  );
  appendEdgeStroke(edgePathElement);
  edgePathElement.style.strokeOpacity = '0';
};

class AggregateEdgeStyle extends EdgeStyleBase {
  override createVisual(context: IRenderContext, edge: IEdge) {
    const g = createSvgElement('g');
    const { sourceNode, targetNode, tag: graphEdge } = edge;
    if (!validateGraphEdge(graphEdge)) {
      logError(Error('Unexpected edge type.'));
      return new SvgVisual(g);
    }
    const path = createPath(edge, false);
    const hoverPath = createPath(edge, true);
    const cache = createRenderDataCache(
      graphEdge,
      path.createSvgPathData(),
      hoverPath.createSvgPathData(),
      sourceNode,
      targetNode
    );
    render(edge, g, path, hoverPath, cache);
    const result = new SvgVisual(g);
    context.setDisposeCallback(result, disposeCallback);
    return result;
  }

  override updateVisual(
    context: IRenderContext,
    oldVisual: Visual,
    edge: IEdge
  ) {
    if (!(oldVisual instanceof SvgVisual)) {
      logError(Error('Invalid visual in AggregateEdgeStyle updateVisual.'));
      return oldVisual;
    }
    const { sourceNode, targetNode, tag: graphEdge } = edge;
    if (!validateGraphEdge(graphEdge)) {
      return oldVisual;
    }
    const container = oldVisual.svgElement;

    const oldCache = container.getAttribute(DATA_RENDER_HASH);
    const path = createPath(edge, false);
    const hoverPath = createPath(edge, true);
    const newCache = createRenderDataCache(
      graphEdge,
      path.createSvgPathData(),
      hoverPath.createSvgPathData(),
      sourceNode,
      targetNode
    );

    if (oldCache === newCache) {
      return oldVisual;
    }

    render(edge, container, path, hoverPath, newCache);
    return oldVisual;
  }

  isHit(_canvasContext: IInputModeContext, location: Point, edge: IEdge) {
    const hitTestRadius = 13; // the hover path is 25px wide, set in CSS.  if the hover path is showing, this should be a hit.
    return this.getPath(edge).pathContains(location, hitTestRadius);
  }

  override getPath(edge: IEdge) {
    return createPath(edge, true);
  }
  protected override lookup(edge: IEdge, type: Constructor) {
    if (type === IObstacleProvider) {
      return new BasicEdgeObstacleProvider(edge);
    }
    return super.lookup(edge, type);
  }
}

export { AggregateEdgeStyle };
